import { getConfigurationEffectiveQuantity, getConfigurationLineTotal } from 'helpers/DealQuantity';
function matchesGroupRestriction(groups, restriction, restrictionValue, restrictionExclusion) {
    if (restriction !== 'SALE_ITEM_GROUP' || !restrictionValue.length) {
        return true;
    }
    const hasMatchingGroups = groups === null || groups === void 0 ? void 0 : groups.some((group) => { var _a; return restrictionValue.includes((_a = group.id) !== null && _a !== void 0 ? _a : ''); });
    if (!hasMatchingGroups) {
        return false;
    }
    const hasExcludedGroup = groups === null || groups === void 0 ? void 0 : groups.some((group) => { var _a; return restrictionExclusion && restrictionExclusion.includes((_a = group.id) !== null && _a !== void 0 ? _a : ''); });
    return !hasExcludedGroup;
}
function addMainItemSubTotal(deal, item, restriction, restrictionValue, restrictionExclusion) {
    var _a;
    if (item.proposed) {
        return 0;
    }
    if (!matchesGroupRestriction(item.item.groups, restriction, restrictionValue, restrictionExclusion)) {
        return 0;
    }
    return getConfigurationLineTotal(item.price, item.quantity, (_a = item.item) === null || _a === void 0 ? void 0 : _a.quantityCalculation, deal);
}
function addMainItemQuantity(deal, item, restriction, restrictionValue, restrictionExclusion) {
    var _a;
    if (item.proposed) {
        return 0;
    }
    if (!matchesGroupRestriction(item.item.groups, restriction, restrictionValue, restrictionExclusion)) {
        return 0;
    }
    return getConfigurationEffectiveQuantity(item.quantity, (_a = item.item) === null || _a === void 0 ? void 0 : _a.quantityCalculation, deal);
}
function addOptionSubTotal(deal, option, restriction, restrictionValue, restrictionExclusion) {
    var _a, _b, _c;
    if (option.proposed) {
        return 0;
    }
    if (!matchesGroupRestriction((_a = option.item) === null || _a === void 0 ? void 0 : _a.groups, restriction, restrictionValue, restrictionExclusion)) {
        return 0;
    }
    return getConfigurationLineTotal((_b = option.feeValue) !== null && _b !== void 0 ? _b : option.price, option.quantity, (_c = option.item) === null || _c === void 0 ? void 0 : _c.quantityCalculation, deal);
}
function addOptionQuantity(deal, option, restriction, restrictionValue, restrictionExclusion) {
    var _a, _b;
    if (option.proposed) {
        return 0;
    }
    if (!matchesGroupRestriction((_a = option.item) === null || _a === void 0 ? void 0 : _a.groups, restriction, restrictionValue, restrictionExclusion)) {
        return 0;
    }
    return getConfigurationEffectiveQuantity(option.quantity, (_b = option.item) === null || _b === void 0 ? void 0 : _b.quantityCalculation, deal);
}
function addPackageSubTotal(deal, pack) {
    var _a, _b;
    if (pack.proposed) {
        return 0;
    }
    return getConfigurationLineTotal((_a = pack.feeValue) !== null && _a !== void 0 ? _a : pack.price, pack.quantity, (_b = pack.package) === null || _b === void 0 ? void 0 : _b.quantityCalculation, deal);
}
function addPackageQuantity(deal, pack) {
    var _a;
    if (pack.proposed) {
        return 0;
    }
    return getConfigurationEffectiveQuantity(pack.quantity, (_a = pack.package) === null || _a === void 0 ? void 0 : _a.quantityCalculation, deal);
}
/**
 * @function getOtherFeeRestrictionSubTotal
 * @since 1.0.0
 */
export function getOtherFeeRestrictionSubTotal(deal, restriction, restrictionValue, restrictionExclusion) {
    var _a;
    let subTotal = 0;
    (_a = deal === null || deal === void 0 ? void 0 : deal.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
        var _a, _b;
        subTotal += addMainItemSubTotal(deal, item, restriction, restrictionValue, restrictionExclusion);
        (_a = item.options) === null || _a === void 0 ? void 0 : _a.forEach((option) => {
            subTotal += addOptionSubTotal(deal, option, restriction, restrictionValue, restrictionExclusion);
        });
        (_b = item.packages) === null || _b === void 0 ? void 0 : _b.forEach((pack) => {
            subTotal += addPackageSubTotal(deal, pack);
        });
    });
    return subTotal;
}
/**
 * @function getOtherFeeRestrictionQuantity
 * @since 1.0.0
 */
export function getOtherFeeRestrictionQuantity(deal, restriction, restrictionValue, restrictionExclusion) {
    var _a;
    let quantity = 0;
    (_a = deal === null || deal === void 0 ? void 0 : deal.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
        var _a, _b;
        quantity += addMainItemQuantity(deal, item, restriction, restrictionValue, restrictionExclusion);
        (_a = item.options) === null || _a === void 0 ? void 0 : _a.forEach((option) => {
            quantity += addOptionQuantity(deal, option, restriction, restrictionValue, restrictionExclusion);
        });
        (_b = item.packages) === null || _b === void 0 ? void 0 : _b.forEach((pack) => {
            quantity += addPackageQuantity(deal, pack);
        });
    });
    return quantity;
}
/**
 * @function getOtherFeeContributingLines
 * @since 1.0.0
 *
 * Lists the deal items, options and packages that enter an automatic other-fee
 * calculation. Uses the same include/exclude rules as
 * `getOtherFeeRestrictionSubTotal` / `getOtherFeeRestrictionQuantity`
 * (proposed lines skipped; SALE_ITEM_GROUP filters items/options; packages
 * are always included when not proposed).
 */
export function getOtherFeeContributingLines(deal, restriction, restrictionValue, restrictionExclusion) {
    var _a;
    const lines = [];
    (_a = deal === null || deal === void 0 ? void 0 : deal.items) === null || _a === void 0 ? void 0 : _a.forEach((item) => {
        var _a, _b, _c;
        if (!item.proposed &&
            matchesGroupRestriction(item.item.groups, restriction, restrictionValue, restrictionExclusion)) {
            lines.push({
                id: `item-${item.id}`,
                name: (_a = item.item.title) !== null && _a !== void 0 ? _a : '',
                type: 'item',
                amount: addMainItemSubTotal(deal, item, restriction, restrictionValue, restrictionExclusion),
                quantity: addMainItemQuantity(deal, item, restriction, restrictionValue, restrictionExclusion)
            });
        }
        (_b = item.options) === null || _b === void 0 ? void 0 : _b.forEach((option) => {
            var _a, _b, _c;
            if (!option.proposed &&
                matchesGroupRestriction((_a = option.item) === null || _a === void 0 ? void 0 : _a.groups, restriction, restrictionValue, restrictionExclusion)) {
                lines.push({
                    id: `option-${option.id}`,
                    name: (_c = (_b = option.item) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : '',
                    type: 'option',
                    amount: addOptionSubTotal(deal, option, restriction, restrictionValue, restrictionExclusion),
                    quantity: addOptionQuantity(deal, option, restriction, restrictionValue, restrictionExclusion)
                });
            }
        });
        (_c = item.packages) === null || _c === void 0 ? void 0 : _c.forEach((pack) => {
            var _a, _b;
            if (pack.proposed) {
                return;
            }
            lines.push({
                id: `package-${pack.id}`,
                name: (_b = (_a = pack.package) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : '',
                type: 'package',
                amount: addPackageSubTotal(deal, pack),
                quantity: addPackageQuantity(deal, pack)
            });
        });
    });
    return lines;
}
/**
 * @function getOtherFeeRestrictionParams
 * @since 1.0.0
 *
 * Mirrors the restriction parsing in `calculateOtherFeePrice`: NONE includes
 * every line; SALE_ITEM_GROUP without a usable restrictionValue is marked
 * unusable so callers keep the stored price instead of listing a wrong base.
 */
export function getOtherFeeRestrictionParams(otherFee) {
    var _a, _b, _c, _d;
    const restriction = (_b = (_a = otherFee.fee) === null || _a === void 0 ? void 0 : _a.restriction) !== null && _b !== void 0 ? _b : 'NONE';
    const restrictionExclusion = parseRestrictionJson((_c = otherFee.fee) === null || _c === void 0 ? void 0 : _c.restrictionExclusion);
    if (restriction === 'NONE') {
        return {
            restriction,
            restrictionValue: [],
            restrictionExclusion,
            usable: true
        };
    }
    if (!((_d = otherFee.fee) === null || _d === void 0 ? void 0 : _d.restrictionValue)) {
        return {
            restriction,
            restrictionValue: [],
            restrictionExclusion,
            usable: false
        };
    }
    const parsed = parseRestrictionJson(otherFee.fee.restrictionValue);
    if (!parsed) {
        return {
            restriction,
            restrictionValue: [],
            restrictionExclusion,
            usable: false
        };
    }
    return {
        restriction,
        restrictionValue: parsed,
        restrictionExclusion,
        usable: true
    };
}
/**
 * Coerce GraphQL / Decimal values to a finite number.
 */
export function toFiniteNumber(value, fallback = 0) {
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : fallback;
}
/**
 * Parse Fee.restrictionValue / restrictionExclusion whether the API returned
 * a JSON string, an already-parsed array, or null.
 */
export function parseRestrictionJson(value) {
    if (value == null || value === '') {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value !== 'string') {
        return undefined;
    }
    try {
        const parsed = JSON.parse(value);
        if (parsed == null) {
            return undefined;
        }
        return Array.isArray(parsed) ? parsed : undefined;
    }
    catch (_a) {
        return undefined;
    }
}
/**
 * @function calculateOtherFeePrice
 * @since 1.0.0
 *
 * Safely (re)compute a deal other-fee price. On any missing / invalid
 * restriction data, keeps the stored price instead of falling back to 0.
 */
export function calculateOtherFeePrice(deal, otherFee) {
    var _a;
    const storedPrice = toFiniteNumber(otherFee.price);
    if (!otherFee.fee ||
        otherFee.fee.value === null ||
        otherFee.fee.value === undefined) {
        return storedPrice;
    }
    const { fee } = otherFee;
    if (fee.type === 'FIXED') {
        return toFiniteNumber(fee.value, storedPrice);
    }
    if (fee.type !== 'PERCENTAGE' && fee.type !== 'QUANTITY') {
        return storedPrice;
    }
    const restriction = (_a = fee.restriction) !== null && _a !== void 0 ? _a : 'NONE';
    const restrictionExclusion = parseRestrictionJson(fee.restrictionExclusion);
    // Mirror backend DealService.calculateFeePrice:
    // - NONE → include all items (restrictionValue ignored)
    // - SALE_ITEM_GROUP without usable restrictionValue → keep stored price
    //   (avoids wiping a valid price when GraphQL drops a JSON array)
    let restrictionValue = [];
    if (restriction === 'NONE') {
        restrictionValue = [];
    }
    else if (!fee.restrictionValue) {
        return storedPrice;
    }
    else {
        const parsed = parseRestrictionJson(fee.restrictionValue);
        if (!parsed) {
            return storedPrice;
        }
        restrictionValue = parsed;
    }
    try {
        if (fee.type === 'PERCENTAGE') {
            return (getOtherFeeRestrictionSubTotal(deal, restriction, restrictionValue, restrictionExclusion) *
                (fee.value / 100));
        }
        return (getOtherFeeRestrictionQuantity(deal, restriction, restrictionValue, restrictionExclusion) * fee.value);
    }
    catch (e) {
        console.error('calculateOtherFeePrice failed, falling back to stored price', e, { otherFee, deal });
        return storedPrice;
    }
}
/**
 * @function recalculateOtherFeesPrices
 * @since 1.0.0
 */
export function recalculateOtherFeesPrices(deal) {
    if (!deal.otherFees) {
        return [];
    }
    return deal.otherFees.map((otherFee) => (Object.assign(Object.assign({}, otherFee), { price: calculateOtherFeePrice(deal, otherFee) })));
}
